// In-app pairing scanner. The same QR Fabrika shows in Settings → Phone relay
// can be scanned here without leaving the PWA. We open the rear camera, decode
// frames (native BarcodeDetector where available — Android Chrome — else the
// bundled jsQR for iOS Safari, which has no BarcodeDetector), and hand the
// decoded `#pair=…` URL back to the router. The payload never leaves the device:
// it lives in the URL fragment, exactly like a camera-app scan would produce.
import jsQR from "jsqr";
import { el } from "../dom.js";
import { parsePairFragment } from "./pair.js";

// BarcodeDetector isn't in the DOM lib types yet; declare the slice we use.
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
interface BarcodeDetectorCtor {
  new (opts?: { formats?: string[] }): BarcodeDetectorLike;
}

// Opens the scanner as a full-page overlay. `onDetected` receives the matched
// `#pair=…` fragment of a valid Fabrika QR; the overlay closes itself first.
export function openScanner(onDetected: (hash: string) => void): void {
  let stopped = false;
  let stream: MediaStream | null = null;
  let rafId = 0;

  const video = el("video", { playsinline: true, autoplay: true }) as HTMLVideoElement;
  // Properties (not just attributes) so iOS Safari honors inline muted autoplay.
  video.muted = true;
  video.playsInline = true;
  const status = el("p", { class: "scanner-status" }, ["Requesting camera…"]);

  const close = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(rafId);
    stream?.getTracks().forEach((t) => t.stop());
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };

  const back = el("button", { class: "scanner-back", onclick: close }, [backIcon(), "Back"]);

  const overlay = el("div", { class: "scanner" }, [
    el("header", { class: "topbar scanner-bar" }, [back]),
    el("div", { class: "scanner-head" }, [
      el("h1", {}, ["Pair New Daemon"]),
      el("p", {}, ["Position the QR code within the frame to authenticate and establish a secure connection."]),
    ]),
    el("div", { class: "scanner-stage" }, [
      el("div", { class: "scanner-frame" }, [
        video,
        el("div", { class: "scanner-glyph" }, [qrGlyph()]),
        el("div", { class: "scanner-reticle" }),
        el("span", { class: "scanner-corner tl" }),
        el("span", { class: "scanner-corner tr" }),
        el("span", { class: "scanner-corner bl" }),
        el("span", { class: "scanner-corner br" }),
        el("div", { class: "scanner-line" }),
      ]),
      status,
    ]),
  ]);

  document.addEventListener("keydown", onKey);
  document.body.append(overlay);

  // A valid scan: drop the camera, close the overlay, then route to pairing.
  const onText = (text: string): boolean => {
    const m = text.match(/#pair=[A-Za-z0-9_-]+/);
    if (!m || !parsePairFragment(m[0])) return false;
    const hash = m[0];
    close();
    onDetected(hash);
    return true;
  };

  void start();

  async function start(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      fail("This browser can't access the camera. Open your phone's camera app and scan the QR there instead.");
      return;
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
    } catch (e) {
      const name = (e as DOMException).name;
      fail(name === "NotAllowedError"
        ? "Camera access was denied. Allow it in your browser settings, or scan the QR with your phone's camera app."
        : "Couldn't start the camera. Scan the QR with your phone's camera app instead.");
      return;
    }
    if (stopped) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    video.srcObject = stream;
    try { await video.play(); } catch { /* autoplay may already be running */ }
    status.textContent = "Scanning for daemon matrix…";

    const Detector = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
    if (Detector) {
      try {
        scanNative(new Detector({ formats: ["qr_code"] }));
        return;
      } catch { /* construction can throw if qr_code is unsupported — fall through */ }
    }
    scanJsqr();
  }

  function scanNative(detector: BarcodeDetectorLike): void {
    const tick = async () => {
      if (stopped) return;
      try {
        const codes = await detector.detect(video);
        for (const c of codes) if (onText(c.rawValue)) return;
      } catch { /* transient decode error — keep going */ }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  }

  function scanJsqr(): void {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const tick = () => {
      if (stopped) return;
      if (ctx && video.readyState >= video.HAVE_ENOUGH_DATA && video.videoWidth) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
        if (code && onText(code.data)) return;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  }

  function fail(message: string): void {
    overlay.querySelector(".scanner-frame")?.classList.add("scanner-frame-error");
    status.className = "scanner-status error";
    status.textContent = message;
  }
}

function backIcon(): HTMLElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", "M15 18l-6-6 6-6");
  svg.append(path);
  return svg as unknown as HTMLElement;
}

// Faint QR-style placeholder shown in the frame's center while scanning.
function qrGlyph(): HTMLElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "64");
  svg.setAttribute("height", "64");
  svg.setAttribute("fill", "currentColor");
  const d =
    "M3 3h7v7H3V3zm2 2v3h3V5H5zm9-2h7v7h-7V3zm2 2v3h3V5h-3zM3 14h7v7H3v-7zm2 2v3h3v-3H5z" +
    "M14 14h2v2h-2v-2zm2 2h2v2h-2v-2zm-2 2h2v2h-2v-2zm4 0h2v2h-2v-2zm0-4h2v2h-2v-2z";
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", d);
  svg.append(path);
  return svg as unknown as HTMLElement;
}
